import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, Globe, Phone, ArrowRight, ArrowUpDown, X, Info, Loader2,
  PhoneIncoming, ShieldCheck, ChevronRight
} from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';
import OrderFormDialog from '../components/OrderFormDialog';
import RegulationsDialog from '../components/RegulationsDialog';
import Flag from '../components/Flag';
import { KycPill } from '../components/RegulationsPanel';
import { api } from '../lib/api';
import {
  DID_PRICING as DID_MOCK,
  TOLLFREE_PRICING as TOLLFREE_MOCK,
  UIFN_PRICING as UIFN_MOCK,
  CAPABILITIES
} from '../data/numberPricing';

/**
 * Pull the three pricing tables from the admin-driven /api/public/virtual-pricing
 * endpoint. Each API row exposes a `.data` payload that already matches the
 * legacy row schema the pricing tables expect (country/code/nrc/mrc/etc.), so
 * we just unwrap it. Falls back to the legacy mock arrays when the API is
 * empty or unreachable, so the page always renders something useful.
 */
function useNumberPricing() {
  const [data, setData] = useState({
    did: DID_MOCK,
    tollfree: TOLLFREE_MOCK,
    uifn: UIFN_MOCK,
    loading: true,
    source: 'mock',
  });

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/public/virtual-pricing')
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        const byType = { did: [], tollfree: [], uifn: [] };
        for (const r of rows) {
          const payload = r.data || r;
          if (r.type && byType[r.type]) byType[r.type].push(payload);
        }
        setData({
          did: byType.did.length ? byType.did : DID_MOCK,
          tollfree: byType.tollfree.length ? byType.tollfree : TOLLFREE_MOCK,
          uifn: byType.uifn.length ? byType.uifn : UIFN_MOCK,
          loading: false,
          source: byType.did.length || byType.tollfree.length || byType.uifn.length ? 'api' : 'mock',
        });
      })
      .catch(() => {
        if (cancelled) return;
        setData((d) => ({ ...d, loading: false, source: 'mock' }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return data;
}

const TABS = [
  {
    key: 'did', label: 'DID Numbers', sub: 'Direct Inward Dialing', icon: PhoneIncoming,
    desc: 'Local, national and mobile numbers in 145+ countries with 2-Way and UC capability.'
  },
  {
    key: 'tollfree', label: 'Toll Free', sub: 'Free for callers', icon: Phone,
    desc: 'Toll-free numbers (0800, 1-800, ITFS) so customers can call you at no charge in their country.'
  },
  {
    key: 'uifn', label: 'UIFN', sub: 'Universal Freephone', icon: Globe,
    desc: 'A single international freephone number that works across multiple countries with one identity.'
  }
];

const FAQ = [
  { q: 'What is a DID number?', a: 'A DID (Direct Inward Dialing) is a phone number that routes inbound calls directly to your VoIP system or specific extension. We offer local, national and mobile DIDs in 145+ countries.' },
  { q: 'Can I receive AND make calls on the same number?', a: 'Yes — numbers marked as 2-Way support both inbound and outbound calls on the same DID. Pricing applies per minute on outbound calls.' },
  { q: 'What does "UC" mean in the table?', a: 'UC numbers are pre-certified to work seamlessly with Unified Communications platforms such as Microsoft Teams (Direct Routing), 3CX, Webex Calling and similar.' },
  { q: 'What is the difference between Toll Free and UIFN?', a: 'A Toll Free number is country-specific (e.g. UK 0800, US 1-800). UIFN is one universal international freephone number that works across many countries with a single 800-prefix identity.' },
  { q: 'Why are some numbers shown as "POA"?', a: 'POA (Price on Application) means that country has restricted carrier access or special documentation requirements. Contact us and we will quote within 24 hours.' },
  { q: 'How long does it take to activate a number?', a: 'Most countries activate within minutes. Some regulated countries require local-presence documentation and take 2–10 business days.' }
];

export default function VirtualNumbers() {
  const [tab, setTab] = useState('did');
  const [orderRow, setOrderRow] = useState(null);
  const [orderType, setOrderType] = useState(null);
  const [regRow, setRegRow] = useState(null);
  const [regType, setRegType] = useState(null);

  // Live data — admin DB first, mock as fallback so the page is never empty.
  const pricing = useNumberPricing();

  const openOrder = (type, row) => {
    setOrderType(type);
    setOrderRow(row);
  };
  const openRegulations = (type, row) => {
    setRegType(type);
    setRegRow(row);
  };

  return (
    <>
      <Seo
        title="Phone Numbers / DID — Local numbers in 145+ countries | VoIPinger"
        description="DID, Toll-Free and UIFN numbers worldwide with transparent pricing. Local presence in 145+ countries, instant activation on most destinations, full IVR control."
        path="/virtual-numbers"
        image="https://voipinger.com/assets/heroes/virtual-numbers.png"
        service={{ name: 'Phone Numbers / DID', serviceType: 'Virtual phone numbers / DID / Toll-Free / UIFN' }}
        faq={FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Phone Numbers / DID', path: '/virtual-numbers' }]}
      />
      <PageHero
        eyebrow="Phone Numbers / DID"
        title="Local numbers in 145+ countries"
        subtitle="Give your business a local presence anywhere in the world. Choose between DIDs, Toll-Free or a single UIFN — same dashboard, transparent pricing."
        breadcrumb="Phone Numbers / DID"
      />

      {/* TAB SELECTOR */}
      <section className="bg-white border-b border-slate-100 sticky top-20 z-30">
        <div className="container-x">
          <div className="grid grid-cols-3 gap-1 md:gap-2 py-3 md:py-4">
            {TABS.map((t) => {
              const active = tab === t.key;
              const Ic = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`group relative rounded-2xl p-3 md:p-5 text-left transition-all duration-200 border ${
                    active
                      ? 'bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 text-white border-transparent shadow-lg shadow-violet-500/30'
                      : 'bg-white border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 text-slate-700'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        active ? 'bg-white/15 text-white' : 'bg-violet-50 text-violet-600 group-hover:bg-white'
                      }`}
                    >
                      <Ic size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className={`font-bold text-sm md:text-base ${active ? 'text-white' : 'text-slate-900'}`}>{t.label}</p>
                      <p className={`text-[11px] md:text-xs ${active ? 'text-violet-100/90' : 'text-slate-500'} hidden sm:block`}>{t.sub}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* TAB CONTENT */}
      {pricing.loading ? (
        <section className="bg-white">
          <div className="container-x section-pad text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading number pricing…
          </div>
        </section>
      ) : (
        <>
          {tab === 'did' && <DidPricingPanel rows={pricing.did} onOrder={(row) => openOrder('did', row)} onRegs={(row) => openRegulations('did', row)} />}
          {tab === 'tollfree' && <TollFreePricingPanel rows={pricing.tollfree} onOrder={(row) => openOrder('tollfree', row)} onRegs={(row) => openRegulations('tollfree', row)} />}
          {tab === 'uifn' && <UifnPricingPanel rows={pricing.uifn} onOrder={(row) => openOrder('uifn', row)} onRegs={(row) => openRegulations('uifn', row)} />}
        </>
      )}

      {/* CAPABILITY LEGEND + FEATURE STRIP */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad grid md:grid-cols-3 gap-6">
          {[
            { i: Globe, t: 'Local presence anywhere', d: 'Customers see a local number when they dial — no international charges, more answered calls.' },
            { i: PhoneIncoming, t: 'Forward to any device', d: 'Route calls to mobiles, desk phones or your softphone. Full IVR control included.' },
            { i: ShieldCheck, t: 'Carrier-grade quality', d: 'Numbers come with HD voice, lawful porting and 99.9% uptime SLA.' }
          ].map((b, i) => (
            <div key={i} className="card-modern">
              <div className="w-12 h-12 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center mb-4">
                <b.i size={20} />
              </div>
              <h3 className="font-bold mb-2">{b.t}</h3>
              <p className="text-sm text-slate-600">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <FaqAccordion items={FAQ} title="Phone Numbers / DID — FAQ" />
      <CtaBanner title="Need a number we don’t list?" subtitle="We can supply numbers from over 145 countries. Tell us what you need." />

      <OrderFormDialog
        open={!!orderRow}
        onClose={() => { setOrderRow(null); setOrderType(null); }}
        type={orderType}
        row={orderRow}
      />

      <RegulationsDialog
        open={!!regRow}
        onClose={() => { setRegRow(null); setRegType(null); }}
        country={regRow?.country}
        type={regType}
        code={regRow?.code}
      />
    </>
  );
}

/* ---------- DID PANEL ---------- */

function DidPricingPanel({ rows, onOrder, onRegs }) {
  const [q, setQ] = useState('');
  const [capFilter, setCapFilter] = useState('all');
  const [sortBy, setSortBy] = useState('country');
  const [sortDir, setSortDir] = useState('asc');

  const list = useMemo(() => {
    let filtered = (rows || []).filter((row) => {
      const country = (row.country || '').toString();
      const code = (row.code || '').toString();
      const inQ = !q || country.toLowerCase().includes(q.toLowerCase()) || code.toLowerCase().includes(q.toLowerCase());
      let inCap = true;
      if (capFilter === 'poa') inCap = row.poa;
      else if (capFilter !== 'all') inCap = !!row[capFilter];
      return inQ && inCap;
    });
    filtered.sort((a, b) => {
      if (sortBy === 'country') return sortDir === 'asc' ? (a.country || '').localeCompare(b.country || '') : (b.country || '').localeCompare(a.country || '');
      const an = a[sortBy] == null ? Number.POSITIVE_INFINITY : a[sortBy];
      const bn = b[sortBy] == null ? Number.POSITIVE_INFINITY : b[sortBy];
      return sortDir === 'asc' ? an - bn : bn - an;
    });
    return filtered;
  }, [rows, q, capFilter, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir('asc'); }
  };

  const cheapest = useMemo(() => (rows || []).filter((r) => r.mrc != null).reduce((m, r) => (m == null || r.mrc < m ? r.mrc : m), null), [rows]);

  return (
    <section className="bg-white">
      <div className="container-x section-pad">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { v: (rows?.length || 0) + '+', l: 'Countries covered' },
            { v: cheapest != null ? '£' + cheapest.toFixed(2) : '£—', l: 'From / month' },
            { v: '£0.005', l: 'Per-min inbound' },
            { v: '< 2 min', l: 'Activation time' }
          ].map((s) => (
            <div key={s.l} className="rounded-2xl bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 p-4 text-center">
              <p className="text-2xl md:text-3xl font-extrabold gradient-text">{s.v}</p>
              <p className="text-xs text-slate-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>

        <SearchBar q={q} setQ={setQ} placeholder="Search a country (e.g. United Kingdom, Germany, US)..." />

        <div className="flex flex-wrap gap-2 mb-6">
          {[{ key: 'all', label: 'All countries' }, ...CAPABILITIES].map((c) => (
            <Chip key={c.key} active={capFilter === c.key} onClick={() => setCapFilter(c.key)}>{c.label}</Chip>
          ))}
        </div>

        <ResultMeta count={list.length} />

        <div className="hidden md:block rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <Th label="Country" sortKey="country" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <th className="px-4 py-3 text-center font-semibold">Capabilities</th>
                  <Th label="Setup" sortKey="nrc" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <Th label="Monthly" sortKey="mrc" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <Th label="Inbound /min" sortKey="inbound" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {list.map((row, i) => (
                  <tr key={`${row.country}-${row.variant || ''}-${i}`} className="border-b border-slate-100 last:border-0 hover:bg-violet-50/30 transition-colors">
                    <CountryCell row={row} type="did" />
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 justify-center">
                        {CAPABILITIES.map((c) => (<CapBadge key={c.key} value={row[c.key]} label={c.label} />))}
                      </div>
                    </td>
                    <PriceCell value={row.nrc} extra={row.setup} poa={row.poa} />
                    <PriceCell value={row.mrc} poa={row.poa} highlight />
                    <PriceCell value={row.inbound} poa={row.poa} decimals={4} />
                    <OrderCell row={row} onOrder={onOrder} onRegs={onRegs} onRegs={onRegs} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="md:hidden space-y-3">
          {list.map((row, i) => (
            <MobileRow
              key={`m-${row.country}-${i}`} row={row} onOrder={onOrder} onRegs={onRegs}
              priceCols={[
                { l: 'Setup', v: row.nrc, extra: row.setup },
                { l: 'Monthly', v: row.mrc, highlight: true },
                { l: 'Inbound/m', v: row.inbound, decimals: 4 }
              ]}
              capabilities
            />
          ))}
        </div>

        {list.length === 0 && <p className="text-center text-slate-500 py-12">No countries match your filters.</p>}
      </div>
    </section>
  );
}

/* ---------- TOLL FREE PANEL ---------- */

function TollFreePricingPanel({ rows, onOrder, onRegs }) {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | poa
  const [sortBy, setSortBy] = useState('country');
  const [sortDir, setSortDir] = useState('asc');

  const list = useMemo(() => {
    let filtered = (rows || []).filter((row) => {
      const country = (row.country || '').toString();
      const code = (row.code || '').toString();
      const inQ = !q || country.toLowerCase().includes(q.toLowerCase()) || code.toLowerCase().includes(q.toLowerCase());
      const inF = filter === 'all' || (filter === 'poa' && row.poa);
      return inQ && inF;
    });
    filtered.sort((a, b) => {
      if (sortBy === 'country') return sortDir === 'asc' ? (a.country || '').localeCompare(b.country || '') : (b.country || '').localeCompare(a.country || '');
      const an = a[sortBy] == null ? Number.POSITIVE_INFINITY : a[sortBy];
      const bn = b[sortBy] == null ? Number.POSITIVE_INFINITY : b[sortBy];
      return sortDir === 'asc' ? an - bn : bn - an;
    });
    return filtered;
  }, [rows, q, filter, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir('asc'); }
  };

  const cheapest = useMemo(() => (rows || []).filter((r) => r.mrc != null).reduce((m, r) => (m == null || r.mrc < m ? r.mrc : m), null), [rows]);
  const live = (rows || []).filter((r) => !r.poa).length;

  return (
    <section className="bg-white">
      <div className="container-x section-pad">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { v: live + '+', l: 'Live destinations' },
            { v: cheapest != null ? '£' + cheapest.toFixed(2) : '£—', l: 'From / month' },
            { v: 'Free', l: 'For caller' },
            { v: '< 24h', l: 'Provisioning' }
          ].map((s) => (
            <div key={s.l} className="rounded-2xl bg-gradient-to-br from-fuchsia-50 to-pink-50 border border-fuchsia-100 p-4 text-center">
              <p className="text-2xl md:text-3xl font-extrabold gradient-text">{s.v}</p>
              <p className="text-xs text-slate-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>

        <SearchBar q={q} setQ={setQ} placeholder="Search a country..." />

        <div className="flex flex-wrap gap-2 mb-6">
          <Chip active={filter === 'all'} onClick={() => setFilter('all')}>All countries</Chip>
          <Chip active={filter === 'poa'} onClick={() => setFilter('poa')}>On request</Chip>
        </div>

        <ResultMeta count={list.length} />

        <div className="hidden md:block rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <Th label="Country" sortKey="country" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="NRC (Setup)" sortKey="nrc" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <Th label="MRC (Monthly)" sortKey="mrc" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <Th label="Landline /min" sortKey="landline" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <Th label="Mobile /min" sortKey="mobile" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {list.map((row, i) => (
                  <tr key={`tf-${row.country}-${row.variant || ''}-${i}`} className="border-b border-slate-100 last:border-0 hover:bg-fuchsia-50/30 transition-colors">
                    <CountryCell row={row} type="tollfree" />
                    <PriceCell value={row.nrc} extra={row.setup} poa={row.poa} />
                    <PriceCell value={row.mrc} poa={row.poa} highlight />
                    <PriceCell value={row.landline} poa={row.poa} decimals={4} naLabel="N/A" />
                    <PriceCell value={row.mobile} poa={row.poa} decimals={4} naLabel="N/A" />
                    <OrderCell row={row} onOrder={onOrder} onRegs={onRegs} onRegs={onRegs} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="md:hidden space-y-3">
          {list.map((row, i) => (
            <MobileRow
              key={`mtf-${row.country}-${i}`} row={row} onOrder={onOrder} onRegs={onRegs}
              priceCols={[
                { l: 'NRC', v: row.nrc, extra: row.setup },
                { l: 'MRC', v: row.mrc, highlight: true },
                { l: 'Landline', v: row.landline, decimals: 4 },
                { l: 'Mobile', v: row.mobile, decimals: 4 }
              ]}
            />
          ))}
        </div>

        {list.length === 0 && <p className="text-center text-slate-500 py-12">No countries match your filters.</p>}
      </div>
    </section>
  );
}

/* ---------- UIFN PANEL ---------- */

function UifnPricingPanel({ rows, onOrder, onRegs }) {
  const [q, setQ] = useState('');
  const [sortBy, setSortBy] = useState('country');
  const [sortDir, setSortDir] = useState('asc');

  const list = useMemo(() => {
    let filtered = (rows || []).filter((row) => {
      const country = (row.country || '').toString();
      const code = (row.code || '').toString();
      return !q || country.toLowerCase().includes(q.toLowerCase()) || code.toLowerCase().includes(q.toLowerCase());
    });
    filtered.sort((a, b) => {
      if (sortBy === 'country') return sortDir === 'asc' ? (a.country || '').localeCompare(b.country || '') : (b.country || '').localeCompare(a.country || '');
      const an = a[sortBy] == null ? Number.POSITIVE_INFINITY : a[sortBy];
      const bn = b[sortBy] == null ? Number.POSITIVE_INFINITY : b[sortBy];
      return sortDir === 'asc' ? an - bn : bn - an;
    });
    return filtered;
  }, [rows, q, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir('asc'); }
  };

  const cheapest = useMemo(() => (rows || []).filter((r) => r.mrc != null).reduce((m, r) => (m == null || r.mrc < m ? r.mrc : m), null), [rows]);

  return (
    <section className="bg-white">
      <div className="container-x section-pad">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { v: (rows?.length || 0) + '+', l: 'Countries supported' },
            { v: cheapest != null ? '£' + cheapest.toFixed(2) : '£—', l: 'From / month' },
            { v: '1 number', l: 'For all countries' },
            { v: '< 24h', l: 'Provisioning' }
          ].map((s) => (
            <div key={s.l} className="rounded-2xl bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 border border-violet-100 p-4 text-center">
              <p className="text-2xl md:text-3xl font-extrabold gradient-text">{s.v}</p>
              <p className="text-xs text-slate-500 mt-1">{s.l}</p>
            </div>
          ))}
        </div>

        <SearchBar q={q} setQ={setQ} placeholder="Search a country..." />
        <ResultMeta count={list.length} />

        <div className="hidden md:block rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                  <Th label="Country" sortKey="country" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="NRC" sortKey="nrc" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <Th label="MRC" sortKey="mrc" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <Th label="Landline /min" sortKey="landline" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <Th label="Mobile /min" sortKey="mobile" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <Th label="Payphone /min" sortKey="payphone" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right" />
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {list.map((row, i) => (
                  <tr key={`u-${row.country}-${i}`} className="border-b border-slate-100 last:border-0 hover:bg-violet-50/30 transition-colors">
                    <CountryCell row={row} type="uifn" />
                    <PriceCell value={row.nrc} extra={row.setup} />
                    <PriceCell value={row.mrc} highlight />
                    <PriceCell value={row.landline} decimals={4} naLabel="N/A" />
                    <PriceCell value={row.mobile} decimals={4} naLabel="N/A" />
                    <PriceCell value={row.payphone} decimals={4} naLabel="N/A" />
                    <OrderCell row={row} onOrder={onOrder} onRegs={onRegs} onRegs={onRegs} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="md:hidden space-y-3">
          {list.map((row, i) => (
            <MobileRow
              key={`mu-${row.country}-${i}`} row={row} onOrder={onOrder} onRegs={onRegs}
              priceCols={[
                { l: 'NRC', v: row.nrc, extra: row.setup },
                { l: 'MRC', v: row.mrc, highlight: true },
                { l: 'Landline', v: row.landline, decimals: 4 },
                { l: 'Mobile', v: row.mobile, decimals: 4 },
                { l: 'Payphone', v: row.payphone, decimals: 4 }
              ]}
            />
          ))}
        </div>

        {list.length === 0 && <p className="text-center text-slate-500 py-12">No countries match your search.</p>}
      </div>
    </section>
  );
}

/* ---------- SHARED HELPERS ---------- */

function SearchBar({ q, setQ, placeholder }) {
  return (
    <div className="flex flex-col md:flex-row gap-3 mb-5">
      <div className="relative flex-1">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder}
          className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 outline-none"
        />
        {q && (
          <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center" aria-label="Clear">
            <X size={14} className="text-slate-500" />
          </button>
        )}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
        active
          ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md shadow-violet-500/30'
          : 'bg-white text-slate-700 border border-slate-200 hover:border-violet-300'
      }`}
    >
      {children}
    </button>
  );
}

function ResultMeta({ count }) {
  return (
    <p className="text-xs text-slate-500 mb-4">
      {count} result{count === 1 ? '' : 's'} • Prices in £ excluding VAT • <span className="text-amber-600 font-semibold">Limited</span> means restricted use cases — contact us for details
    </p>
  );
}

function Th({ label, sortKey, sortBy, sortDir, onSort, align = 'left' }) {
  const active = sortBy === sortKey;
  return (
    <th className={`px-4 py-3 font-semibold cursor-pointer select-none text-${align} hover:text-violet-700`} onClick={() => onSort(sortKey)}>
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown size={12} className={`transition-opacity ${active ? 'opacity-100 text-violet-600' : 'opacity-30'}`} />
      </span>
    </th>
  );
}

function CountryCell({ row, type }) {
  return (
    <td className="px-4 py-3 whitespace-nowrap">
      <div className="flex items-center gap-3">
        <Flag code={row.code} size="md" />
        <div>
          <p className="font-semibold text-slate-900">{row.country}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            {row.variant && <span className="text-[11px] text-violet-600 font-medium uppercase tracking-wider">{row.variant}</span>}
            {type && <KycPill country={row.country} type={type} />}
          </div>
        </div>
      </div>
    </td>
  );
}

function CapBadge({ value, label }) {
  if (!value) return <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-slate-100 text-slate-300">{label}</span>;
  if (value === 'limited') {
    return <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200" title="Limited availability">{label}</span>;
  }
  return <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-violet-100 text-violet-700 border border-violet-200">{label}</span>;
}

function PriceCell({ value, extra, poa, highlight, suffix = '', decimals = 2, naLabel = 'POA' }) {
  if (poa) {
    return (
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <span className="text-xs font-semibold text-slate-400 italic">POA</span>
      </td>
    );
  }
  if (value == null) {
    return (
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <span className="text-xs font-semibold text-slate-400">{naLabel}</span>
      </td>
    );
  }
  return (
    <td className="px-4 py-3 text-right whitespace-nowrap">
      <span className={`font-semibold ${highlight ? 'text-violet-700' : 'text-slate-900'}`}>£{value.toFixed(decimals)}{suffix}</span>
      {extra > 0 && <span className="block text-[10px] text-slate-400">+£{extra.toFixed(4)} extra</span>}
    </td>
  );
}

function OrderCell({ row, onOrder, onRegs }) {
  return (
    <td className="px-4 py-3 text-right whitespace-nowrap">
      <div className="inline-flex items-center gap-1.5">
        {onRegs && (
          <button
            onClick={() => onRegs(row)}
            aria-label={`Regulations for ${row.country}`}
            title="View regulations & required documents"
            className="w-8 h-8 rounded-full bg-slate-50 hover:bg-violet-50 hover:text-violet-700 text-slate-500 border border-slate-200 hover:border-violet-200 flex items-center justify-center transition-colors"
          >
            <Info size={14} />
          </button>
        )}
        <button
          onClick={() => onOrder(row)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:shadow-md hover:shadow-violet-500/30 transition-shadow"
        >
          Order <ChevronRight size={14} />
        </button>
      </div>
    </td>
  );
}

function MobileRow({ row, priceCols, capabilities, onOrder, onRegs }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <Flag code={row.code} size="lg" />
          <div className="min-w-0">
            <p className="font-bold text-slate-900 truncate">{row.country}</p>
            {row.variant && <p className="text-[11px] text-violet-600 font-medium uppercase tracking-wider">{row.variant}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {onRegs && (
            <button
              onClick={() => onRegs(row)}
              aria-label={`Regulations for ${row.country}`}
              className="w-8 h-8 rounded-full bg-slate-50 hover:bg-violet-50 text-slate-500 hover:text-violet-700 border border-slate-200 flex items-center justify-center"
            >
              <Info size={14} />
            </button>
          )}
          <button onClick={() => onOrder(row)} className="text-white bg-gradient-to-r from-violet-600 to-fuchsia-600 rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1">
            Order <ChevronRight size={12} />
          </button>
        </div>
      </div>
      {capabilities && (
        <div className="flex flex-wrap gap-1 mb-3">
          {CAPABILITIES.map((c) => (<CapBadge key={c.key} value={row[c.key]} label={c.label} />))}
        </div>
      )}
      <div className={`grid gap-2 text-center pt-3 border-t border-slate-100`} style={{ gridTemplateColumns: `repeat(${priceCols.length}, minmax(0,1fr))` }}>
        {priceCols.map((p) => (
          <MiniPrice key={p.l} label={p.l} value={p.v} extra={p.extra} poa={row.poa} highlight={p.highlight} decimals={p.decimals} />
        ))}
      </div>
    </div>
  );
}

function MiniPrice({ label, value, extra, poa, highlight, decimals = 2 }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      {poa ? (
        <p className="text-sm font-semibold text-slate-400 italic">POA</p>
      ) : value == null ? (
        <p className="text-sm font-semibold text-slate-400">N/A</p>
      ) : (
        <>
          <p className={`text-sm font-bold ${highlight ? 'text-violet-700' : 'text-slate-900'}`}>£{value.toFixed(decimals)}</p>
          {extra > 0 && <p className="text-[9px] text-slate-400">+£{extra.toFixed(4)}</p>}
        </>
      )}
    </div>
  );
}
